import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
} from '../constants/worldcupReactQuery.constants';
import { fetchMemberProfile, fetchParticipantProfile } from '../helpers/worldCupMemberProfileApi.helper';
import type { MemberProfileData } from '../helpers/worldCupMemberProfileApi.helper';

export type { MemberProfileData };

/**
 * Fetch a group member's profile (group-scoped auth: viewer must share the group).
 * Pass groupId='' to use the global endpoint (participant-only auth).
 */
export function useWorldCupMemberProfile(
  competitionId: string,
  groupId: string,
  memberId: string,
  options?: { enabled?: boolean },
) {
  const apiClient = useApiClient();
  const enabled =
    (options?.enabled ?? true) &&
    competitionId.length > 0 &&
    memberId.length > 0;

  const useGlobal = groupId.length === 0;

  return useQuery({
    queryKey: useGlobal
      ? worldCupQueryKeys.participantProfile(competitionId, memberId)
      : worldCupQueryKeys.memberProfile(competitionId, groupId, memberId),
    queryFn: () =>
      useGlobal
        ? fetchParticipantProfile(apiClient, competitionId, memberId)
        : fetchMemberProfile(apiClient, competitionId, groupId, memberId),
    enabled,
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
  });
}
